<!-- src/views/management/users-edit-[id].vue -->
<script setup lang="ts">
import LogoutTime from '@/layouts/components/LogoutTime.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'
import { useSnackBar } from '@/utils/stores/snackbar'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: { layout: 'blank', action: 'write', subject: 'users' },
})

const router = useRouter()
const route  = useRoute()
const snackbarStore = useSnackBar()

/* ── 상단 ‘관리’ 탭 라우팅 동기화 ───────────────────────── */
type MgmtTab = 'permissions' | 'users' | 'sites'
const mgmtTab = ref<MgmtTab>('users')
function syncTabFromRoute() {
  const p = route.path || ''
  if (p.includes('/manage/permissions')) mgmtTab.value = 'permissions'
  else if (p.includes('/manage/sites'))  mgmtTab.value = 'sites'
  else                                   mgmtTab.value = 'users'
}
onMounted(syncTabFromRoute)
watch(() => route.fullPath, syncTabFromRoute)
watch(mgmtTab, v => {
  const next =
    v === 'permissions' ? '/manage/permissions'
  : v === 'sites'       ? '/manage/sites'
  :                       '/manage/users'
  if (route.path !== next) router.push(next)
})

/* ── 헤더 hover ───────────────────────────────────────── */
const hover = ref(false)

/* ── 폼 모델 ──────────────────────────────────────────── */
const form = reactive({
  name: '',
  email: '',
  siteId: '',
  roleId: '',
})
const refForm = ref<any>(null)

/* ── 셀렉트 데이터 ────────────────────────────────────── */
const siteItems = ref<{ title: string; value: string }[]>([])
const roleItems = ref<{ title: string; value: string }[]>([])

/* ── 데이터 로드: 기관/역할 목록 ──────────────────────── */
const { data: sitesRes } = await useApi(createUrl('/management/sites', { query: {} }))
if (Array.isArray(sitesRes.value)) {
  siteItems.value = sitesRes.value.map((s: any) => ({
    title: s.name ?? s.siteName ?? `Site ${s.id}`,
    value: String(s.id ?? s.siteId ?? ''),
  }))
}
const { data: rolesRes } = await useApi(createUrl('/management/roles/all', { query: {} }))
if (Array.isArray(rolesRes.value)) {
  roleItems.value = rolesRes.value.map((r: any) => ({ title: r.realName, value: String(r.id) }))
}

/* ── 대상 사용자 로드 ─────────────────────────────────── */
const id = route.params.id as string
const { data: userRes, error: userErr } = await useApi(createUrl(`/management/users/${id}`))
if (!userErr.value && userRes.value) {
  // 백엔드 키 다양성에 대비한 안전 매핑
  const u: any = userRes.value
  form.name  = u.name  ?? u.userName ?? ''
  form.email = u.email ?? u.userEmail ?? ''
  form.siteId = String(u.siteId ?? u.site_id ?? u.site?.id ?? '')
  form.roleId   = String(u.role_id ?? u.roleId ?? '')
}

/* ── 검증/저장 ───────────────────────────────────────── */
const required = (v: any) => (!!v || v === 0) || '필수 입력입니다.'
const emailRule = (v: string) =>
  (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? true : '이메일 형식이 올바르지 않습니다.')

const saving = ref(false)
const canSubmit = computed(() =>
  form.name.trim() && form.email.trim() && form.siteId && form.roleId && !saving.value,
)

const goBack = () => {
  sessionStorage.setItem('manage-users-returning', '1')
  router.push('/manage/users')
}

const saveUser = async () => {
  const validate = await refForm.value?.validate()
  if (!validate?.valid) return

  saving.value = true

  await $api(`/management/users/${id}`, {
    method: 'PATCH',
    body: {
        name: form.name,
        email: form.email,
        site_id: form.siteId,
        role_id: form.roleId,
    },
  })
  snackbarStore.showSnackBar('사용자 정보를 저장했어요.')
  saving.value = false
  goBack()
}
</script>

<template>
  <!-- 상단 헤더 -->
  <VCard class="topbar-card" style="border-radius:0;">
  <VCardText class="d-flex justify-space-between align-between py-2">
    <!-- 👇 로고 버튼 (왼쪽) -->
    <div
      class="d-flex align-center cursor-pointer"
      @click="router.push('/projects')"
    >
      <img
        src="/images/home_logo.png"
        alt="홈"
        width="87"
        height="30"
        style="display:block"
      />
    </div>

    <!-- 오른쪽 기존 요소 -->
    <div class="d-flex align-center">
      <LogoutTime class="me-2" />
      <UserProfile />
      <div class="divider-vert" />
      <div
        class="mgmt-quick d-flex align-center px-2 py-1"
        :style="{ backgroundColor: hover ? 'rgba(0,0,0,0.05)' : 'transparent' }"
        @click="router.push('/manage/permissions')"
        @mouseover="hover = true"
        @mouseleave="hover = false"
      >
        <img src="/images/settings.png" alt="관리" class="icon-20 me-1" />
        <span class="text-gray-600">관리</span>
      </div>
    </div>
  </VCardText>
</VCard>


  <!-- 페이지 타이틀 -->
  <div class="page-title-wrap">
    <h2 class="page-title">관리</h2>
  </div>

  <!-- 관리 탭 -->
  <div class="mgmt-tabs">
    <VBtnToggle v-model="mgmtTab" class="tab-toggle" mandatory divided borderless>
      <VBtn :value="'permissions'" class="tab-btn">권한 관리</VBtn>
      <VBtn :value="'users'"       class="tab-btn">전체 사용자 관리</VBtn>
      <VBtn :value="'sites'"       class="tab-btn">전체 기관 관리</VBtn>
    </VBtnToggle>
  </div>

  <!-- 본문 -->
  <VRow class="page-wrap">
    <VCol cols="12" sm="12" md="10" lg="8" class="mx-auto">
      <VCard class="panel">
        <div class="panel-title">사용자 수정</div>

        <div class="fieldset">
          <div class="fieldset-head">
            <span class="fieldset-title">사용자 정보</span>
          </div>

          <VForm ref="refForm" @submit.prevent>
            <!-- 사용자 이름 -->
            <div class="form-row">
              <div class="form-label">사용자 이름 <span class="req">*</span></div>
              <AppTextField v-model="form.name" hide-details="auto" :rules="[required]" />
            </div>

            <!-- 이메일 주소 -->
            <div class="form-row">
              <div class="form-label">이메일 주소 <span class="req">*</span></div>
              <AppTextField v-model="form.email" hide-details="auto" :rules="[required, emailRule]" />
            </div>

            <!-- 기관 선택 -->
            <div class="form-row">
              <div class="form-label">기관 <span class="req">*</span></div>
              <AppSelect
                v-model="form.siteId"
                :items="siteItems"
                placeholder="기관 선택"
                hide-details="auto"
                :rules="[required]"
              />
            </div>

            <!-- 역할 선택 -->
            <div class="form-row">
              <div class="form-label">역할 <span class="req">*</span></div>
              <AppSelect
                v-model="form.roleId"
                :items="roleItems"
                placeholder="역할 선택"
                hide-details="auto"
                :rules="[required]"
              />
            </div>

            <!-- 액션 -->
            <div class="form-actions">
              <VBtn
                class="btn-primary"
                color="#3857F5"
                :disabled="!canSubmit"
                :loading="saving"
                @click="saveUser"
              >
                저장
              </VBtn>
              <VBtn class="btn-ghost" color="secondary" variant="tonal" @click="goBack">취소</VBtn>
            </div>
          </VForm>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped>
/* 헤더 */
.divider-vert{ background-color: rgba(0,0,0,10%); block-size: 30px; inline-size: 1px; margin-inline: 15px; }
.icon-20{ block-size:20px; inline-size:20px; }
.text-gray-600{ color:#4B5563; font-size:14px; }
.mgmt-quick{ border-radius:6px; cursor:pointer; transition:background-color .2s; }

/* 페이지 타이틀 */
.page-title-wrap{ padding-block: 50px 0; padding-inline: 34px; }
.page-title{ margin:0; border-block-end: 1px solid #DAE6EE; color:#111827; font-size:22px; font-weight:700; margin-block-end: 20px; padding-block-end: 20px; }

/* 탭 */
.mgmt-tabs{ display:flex; justify-content:flex-start; padding-block:10px 8px; padding-inline:34px; }
.tab-toggle{ border:1px solid #E8ECEF; border-radius:8px; background:#f4f6fa; box-shadow:0 3px 8px rgba(201,201,201,25%); }
.tab-btn{ min-inline-size:160px; }
.tab-btn.v-btn--active{ background:#eef0ff !important; }

/* 본문/패널 */
.page-wrap{ margin-block:14px 28px; margin-inline:34px; }
.panel{ overflow:hidden; border-radius:14px; background:#fff; box-shadow:0 10px 24px rgba(17,24,39,6%); }
.panel-title{ color:#111827; font-size:16px; font-weight:700; padding-block:18px 0; padding-inline:24px; }

/* 필드셋 */
.fieldset{ padding-block:14px 24px; padding-inline:24px; }
.fieldset-head{ border-block-end:1px solid #EEF2F7; margin-block-end:16px; padding-block:12px 14px; }
.fieldset-title{ color:#5B6372; font-size:13px; font-weight:700; }

/* 폼 레이아웃 */
.form-row{ display:grid; align-items:center; gap:12px; grid-template-columns: 180px 1fr; margin-block:10px; }
.form-label{ color:#475569; font-size:14px; font-weight:600; }
.req{ color:#ef4444; }

/* 인풋 톤 맞추기 */
:deep(.v-field){ border-radius:10px; }
:deep(.v-field__input){ min-block-size:42px; }

/* 액션 버튼 */
.form-actions{ display:flex; justify-content:center; gap:12px; margin-block-start:18px; }
.btn-primary{ border-radius:10px; block-size:40px !important; min-inline-size:120px; }
.btn-ghost{ border-radius:10px; block-size:40px !important; min-inline-size:120px; }

@media (max-width: 720px){
  .form-row{ grid-template-columns: 1fr; }
}
</style>
